import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import {
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { density, shape } from "@app/design-tokens";
import { useState } from "react";
import { Link } from "react-router";

import { useAuth } from "@/auth/authContext";
import { BrandMark } from "@/components/BrandMark";
import { useUiStore } from "@/lib/uiStore";

/**
 * 主內容卡片的頂列。
 *
 * 刻意**沒有**全站搜尋框。設計稿上有，但後端目前沒有搜尋端點 ——
 * 放一個按了沒反應的搜尋框比不放更糟。等 `/search/` 這類端點出現再補。
 * （品項清單頁有自己的搜尋框，那個是真的接到後端的 `search` 參數。）
 *
 * 左邊那一格的內容由 AppShell 決定：桌機在有第二層的分節顯示收合按鈕，
 * 手機沒有第二層可收，所以改成顯示標誌（手機沒有 rail，否則整站看不到標誌）。
 * 兩者都不給的話那一格就是空的，這比放一個按了沒反應的按鈕好。
 */
interface TopBarProps {
  /** 給了才顯示「切換第二層」按鈕 —— 沒有第二層的分節不該有這個按鈕 */
  onToggleNav?: () => void;
  /** 顯示標誌（手機版用，桌機的標誌在 rail 上） */
  showBrand?: boolean;
  /**
   * 收下「頁面標題」那一格的 DOM 節點。
   *
   * 標題與副標由頁面的 `PageHeader` 用 portal 畫進來（見 `pageTitleSlot.ts`）。
   * 頂列自己不知道現在是哪一頁，也不該知道。
   */
  titleSlotRef?: (node: HTMLElement | null) => void;
}

export function TopBar({ onToggleNav, showBrand = false, titleSlotRef }: TopBarProps) {
  const { me } = useAuth();
  const themeMode = useUiStore((state) => state.themeMode);
  const setThemeMode = useUiStore((state) => state.setThemeMode);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const isDark = themeMode === "dark";
  const displayName = me?.display_name ?? "";

  return (
    <Box
      component="header"
      sx={{
        height: density.topBarHeight,
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {onToggleNav && (
        <Tooltip title="切換第二層選單">
          <IconButton
            onClick={onToggleNav}
            aria-label="切換第二層選單"
            sx={{ bgcolor: "action.hover", borderRadius: `${shape.medium}px` }}
          >
            <MenuOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {showBrand && (
        <Box component={Link} to="/" aria-label="回總覽" sx={{ display: "flex", alignItems: "center" }}>
          <BrandMark size={26} />
        </Box>
      )}

      {/*
        頁面標題那一格。空的時候（例如 404、沒有權限這些不畫 PageHeader 的
        頁面）它只是一塊撐開空間的區域，頂列不會塌掉。
      */}
      <Box ref={titleSlotRef} sx={{ flexGrow: 1, minWidth: 0, pl: 0.5 }} />

      <Tooltip title={isDark ? "切換為淺色" : "切換為深色"}>
        <IconButton
          onClick={() => setThemeMode(isDark ? "light" : "dark")}
          aria-label="切換深淺色"
        >
          {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ my: 2 }} />

      <Box
        component="button"
        type="button"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        aria-haspopup="menu"
        aria-expanded={Boolean(anchor)}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          border: 0,
          background: "none",
          font: "inherit",
          color: "inherit",
          cursor: "pointer",
          borderRadius: `${shape.medium}px`,
          px: 0.5,
          py: 0.5,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <Avatar
          sx={{
            width: 34,
            height: 34,
            bgcolor: "action.selected",
            color: "text.secondary",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {displayName.slice(0, 1)}
        </Avatar>
        <Box sx={{ display: { xs: "none", sm: "flex" }, flexDirection: "column", alignItems: "flex-start" }}>
          <Typography variant="subtitle2">{displayName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {me?.department || me?.email || me?.mobile}
          </Typography>
        </Box>
        <KeyboardArrowDownIcon fontSize="small" sx={{ color: "text.secondary" }} />
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220, border: 1, borderColor: "divider" } } }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2">{displayName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {me?.email ?? me?.mobile}
          </Typography>
        </Box>
        <Divider />
        {/*
          個人中心在 rail／底部列已經有入口，這裡再放一個是刻意的重複：
          「改我自己的資料」這件事，多數人第一個會找的地方是右上角的帳號選單。
        */}
        <MenuItem component={Link} to="/account" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <PersonOutlineOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="個人中心" slotProps={{ primary: { variant: "subtitle2" } }} />
        </MenuItem>
        {/*
          登出刻意不在這個選單裡，放在個人中心頁的右下角 —— 不要再加回來，
          兩處都有的話之後改登出流程（例如加確認）很容易只改到一邊。
        */}
        <MenuItem component={Link} to="/account/guide" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <MenuBookOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="使用指南" slotProps={{ primary: { variant: "subtitle2" } }} />
        </MenuItem>
      </Menu>
    </Box>
  );
}
